/**
 * Mixin for warehouse document dialog components
 * (WaybillDialog, WriteOffDialog, ReturnDialog, PurchaseOrderDialog).
 *
 * Provides common imports, data, methods, computed, and watchers shared
 * across all document dialog components.
 *
 * Provides:
 *   - mixins: hasModel, hasValidation, hasPrinting, hasSnackbar, hasSelectedWarehouse, hasWarehouseUsers
 *   - components: ChooseWarehouseField, WarehouseDocumentGroceriesTable, FullscreenDialog, WarehouseButtonPrintMenu
 *   - data: valid, loading
 *   - methods: removeGrocery(), getPrintHeaders(withStock), getPrintItems()
 *   - computed: headers
 *   - watch: model → fetchUsers (immediate)
 *
 * Each dialog still provides its own:
 *   - template (form fields, dialog props differ per type)
 *   - save() method
 *   - print() method (uses getPrintHeaders/getPrintItems helpers)
 *   - any extra components, props, data, computed, watchers
 */
import hasModel from "@/mixins/hasModel";
import hasValidation from "@/mixins/hasValidation";
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasSelectedWarehouse from "@/mixins/warehouse/hasSelectedWarehouse";
import hasWarehouseUsers from "@/mixins/warehouse/hasWarehouseUsers";
import ChooseWarehouseField from "@/components/warehouse/fields/ChooseWarehouseField.vue";
import WarehouseDocumentGroceriesTable from "@/components/warehouse/shared/WarehouseDocumentGroceriesTable.vue";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import WarehouseButtonPrintMenu from "@/components/warehouse/buttons/WarehouseButtonPrintMenu.vue";

export default {
    mixins: [
        hasModel,
        hasValidation,
        hasPrinting,
        hasSnackbar,
        hasSelectedWarehouse,
        hasWarehouseUsers,
    ],
    components: {
        ChooseWarehouseField,
        WarehouseDocumentGroceriesTable,
        FullscreenDialog,
        WarehouseButtonPrintMenu,
    },
    data() {
        return {
            valid: false,
            loading: false,
        };
    },
    methods: {
        removeGrocery(grocery) {
            this.editedItem.groceries = this.editedItem.groceries.filter(
                (item) => grocery.id !== item.id
            );
        },
        /**
         * Prepares headers for printing — removes actions column,
         * optionally shows warehouse_stock column.
         * When withBrutoNeto is true (default), replaces "quantity" with bruto/neto split and adds price & amount.
         * When false, returns base headers as-is (amount column kept directly).
         */
        getPrintHeaders(withStock = false, withBrutoNeto = true) {
            const base = this.headers
                .filter((h) => h.value !== "actions" && h.value !== "quantity")
                .map((h) =>
                    withStock && h.value === "warehouse_stock"
                        ? { ...h, print: undefined }
                        : h
                );
            if (!withBrutoNeto) {
                return base;
            }
            return [
                ...base,
                { text: this.$t("warehouse.quantity_gross"), value: "quantity_gross", align: "right", group: this.$t("quantity") },
                { text: this.$t("warehouse.quantity_net"), value: "quantity_net", align: "right", group: this.$t("quantity") },
                { text: this.$t("price"), value: "price", align: "right" },
                { text: this.$t("amount"), value: "amount", align: "right" },
            ];
        },
        /**
         * Prepares grocery items for printing — adds row numbers.
         * When withBrutoNeto is true (default), maps quantity to neto and leaves bruto/price/amount empty.
         * When false, keeps amount as-is.
         */
        getPrintItems(withBrutoNeto = true) {
            return this.editedItem.groceries.map((item, index) => {
                if (!withBrutoNeto) {
                    return { ...item, num: index + 1 };
                }
                return {
                    ...item,
                    num: index + 1,
                    quantity_gross: "",
                    quantity_net: item.amount,
                    price: "",
                    amount: "",
                };
            });
        },
        /**
         * Returns the common beforeTitleHtml block used in all document print dialogs.
         * Shows warehouse name and document date.
         */
        getPrintBeforeTitleHtml() {
            return `
                <h2 style="margin-bottom: 0px">${this.$t("stock")}: ${this.toLatin(this.editedItem.warehouse?.name || "")}</h2>
                <div style="font-size: 16px;margin-top: 20px;">
                    ${this.$t("date")}: ${this.localeDate(this.editedItem.document_date)}
                </div>
            `;
        },
        /**
         * Returns the note HTML block for printing, or empty string if no notes.
         */
        getPrintNoteHtml() {
            if (!this.editedItem.notes) return "";
            return `
                <div style="font-size: 12px;margin-top: 20px;">
                    ${this.$t("note")}: ${this.editedItem.notes}
                </div>
            `;
        },
        /**
         * Looks up a user by ID and returns their formatted full name (toLatin).
         * @param {number|string} userId
         * @returns {string}
         */
        getUserFullName(userId) {
            const user = this.users.find((u) => u.id === userId);
            if (!user) return "";
            return [user.name, user.lastname].map((n) => this.toLatin(n)).join(" ");
        },
    },
    computed: {
        headers() {
            return [
                { text: this.$t("r_b"), value: "num", width: "100px" },
                {
                    text: this.$t("code"),
                    align: "start",
                    value: "code",
                    width: "150px",
                },
                { text: this.$t("title"), value: "name" },
                {
                    text: this.$t("amount"),
                    value: "amount",
                    align: "right",
                    width: "200px",
                },
                {
                    text: this.$t("warehouse.warehouse_status"),
                    value: "warehouse_stock",
                    print: false,
                    align: "right",
                    width: "200px",
                },
                {
                    text: this.$t("unit_of_measurment"),
                    printText: this.$t("unit_of_measurment").replace(" ", "<br>"),
                    value: "measure",
                    width: "200px",
                },
                { text: "", value: "actions", width: "100px" },
            ];
        },
    },
    watch: {
        model: {
            handler() {
                if (!this.users.length) {
                    this.fetchUsers();
                }
            },
            immediate: true,
        },
    },
};
